import { createContext, useCallback, useMemo, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  strandedRuleIdsByStep,
  type ProcessDraftAssignment,
  type ProcessDraftStep,
  type PublishValidationError
} from '@bliks-io/contracts';
import type { ProcessFlowEditorProps, ProcessFlowRevealRequest } from '@bliks-io/ui';
import { usePreviousValue } from '@/hooks/renderPhaseState';
import type { UndoableEditorActions } from './actions/useUndoableEditorActions';
import type { DraftEditorEvent } from './draftEvents';
import {
  buildDraftIssues,
  isOutputGroupStranded,
  type DraftIssue,
  type DraftIssueField,
  type DraftIssueQuickFix
} from './publish/draftIssues';

export interface DraftIssueFocusRequest {
  nodeId: string;
  field: DraftIssueField;
  /** New per request, so a repeat request focuses the field again. */
  id: number;
}

export interface DraftIssuesContextValue {
  issuesByNodeId: ReadonlyMap<string | null, readonly DraftIssue[]>;
  focusRequest: DraftIssueFocusRequest | null;
  goToIssue: (issue: DraftIssue) => void;
  runQuickFix: (fix: DraftIssueQuickFix) => void;
  /** Clears the focus request once its field took focus; stale ids are ignored. */
  onFocusHandled: (requestId: number) => void;
  /** Ref for an issue button: moves focus into the editor when it unmounts while focused. */
  keepFocusInEditor: (element: HTMLElement | null) => () => void;
}

/** Null outside the draft editor: no issues, no focus. */
export const DraftIssuesContext = createContext<DraftIssuesContextValue | null>(null);

type EditorMarkers = Pick<
  ProcessFlowEditorProps,
  'errorNodeIds' | 'incompleteRuleIdsByStep' | 'disconnectedEventIds'
>;

// Undefined, not an empty collection: `decorate` depends on these, and a fresh
// empty one would re-decorate the canvas for a no-op.
function nonEmpty<T extends { size: number }>(collection: T): T | undefined {
  return collection.size > 0 ? collection : undefined;
}

interface UseDraftIssuesArgs {
  /** Undefined until the draft payload has loaded. */
  draftId: string | undefined;
  validation: PublishValidationError;
  steps: readonly ProcessDraftStep[];
  draftAssignments: readonly ProcessDraftAssignment[];
  draftEvents: readonly DraftEditorEvent[];
  stepNumberByStableId: ReadonlyMap<string, number>;
  undoableActions: Pick<UndoableEditorActions, 'markStepAsTerminal' | 'markRulesAsTerminal'>;
  setSelectedStepId: (nodeId: string | null) => void;
  containerRef: RefObject<HTMLElement | null>;
  /** The sidebar and its markers show only beside a mounted canvas, never beside the loader. */
  isCanvasReady: boolean;
}

/**
 * The editor's publish issues, the sidebar's open state, the canvas markers,
 * and go-to-issue and quick-fix wiring for the sidebar and the detail panels.
 */
export function useDraftIssues({
  draftId,
  validation,
  steps,
  draftAssignments,
  draftEvents,
  stepNumberByStableId,
  undoableActions,
  setSelectedStepId,
  containerRef,
  isCanvasReady
}: UseDraftIssuesArgs) {
  const { t } = useTranslation();
  const { markStepAsTerminal, markRulesAsTerminal } = undoableActions;

  const liveGroups = useMemo(
    () =>
      buildDraftIssues({
        validation,
        steps,
        draftAssignments,
        draftEvents,
        stepNumberByStableId,
        t
      }),
    [validation, steps, draftAssignments, draftEvents, stepNumberByStableId, t]
  );
  // The live list alone gates Publish: when it disagrees with the server, the server judges.
  const issueCount = liveGroups.reduce((sum, group) => sum + group.issues.length, 0);

  // Each loaded draft starts with the sidebar closed; a discard-and-recreate
  // brings a new id.
  const [isOpen, setIsOpen] = useState(false);
  // A server rejection the live list cannot explain: a bundle built before the
  // backend's validator changed sees no issues, so the sidebar lists the server's.
  const [rejection, setRejection] = useState<PublishValidationError | null>(null);
  const previousDraftId = usePreviousValue(draftId);
  if (draftId !== previousDraftId) {
    setIsOpen(false);
    setRejection(null);
  }
  if (rejection && issueCount > 0) {
    setRejection(null);
  }

  // Display only: the server judged an older draft, and a quick fix built from
  // its reasons could cascade over connections made since.
  const rejectionGroups = useMemo(
    () =>
      rejection &&
      buildDraftIssues({
        validation: rejection,
        steps,
        draftAssignments,
        draftEvents,
        stepNumberByStableId,
        t
      }).map((group) => ({
        ...group,
        issues: group.issues.map((issue) => ({ ...issue, quickFix: undefined }))
      })),
    [rejection, steps, draftAssignments, draftEvents, stepNumberByStableId, t]
  );
  const groups = rejectionGroups ?? liveGroups;

  const [revealRequest, setRevealRequest] = useState<ProcessFlowRevealRequest>();
  const [focusRequest, setFocusRequest] = useState<DraftIssueFocusRequest | null>(null);
  const requestIdRef = useRef(0);

  // An undo can remove the node; its panel then never mounts to consume the request.
  const focusNodeId = focusRequest?.nodeId;
  if (
    focusNodeId !== undefined &&
    !steps.some((step) => String(step.stableStepId) === focusNodeId) &&
    !draftAssignments.some((assignment) => String(assignment.stableStepId) === focusNodeId) &&
    !draftEvents.some((event) => event._id === focusNodeId)
  ) {
    setFocusRequest(null);
  }

  const goToIssue = useCallback(
    ({ nodeId, field }: DraftIssue) => {
      if (nodeId === null) return;
      requestIdRef.current += 1;
      const id = requestIdRef.current;
      setSelectedStepId(nodeId);
      setRevealRequest({ id, nodeId });
      setFocusRequest(field ? { nodeId, field, id } : null);
    },
    [setSelectedStepId]
  );

  const onFocusHandled = useCallback((requestId: number) => {
    setFocusRequest((current) => (current?.id === requestId ? null : current));
  }, []);

  const strandedRuleIds = useMemo(
    () => strandedRuleIdsByStep(steps, draftAssignments),
    [steps, draftAssignments]
  );

  const runQuickFix = useCallback(
    (fix: DraftIssueQuickFix) => {
      if (
        fix.kind === 'markRulesEnd' &&
        !isOutputGroupStranded(strandedRuleIds, fix.stepId, fix.ruleIds)
      ) {
        return;
      }
      // The clicked button unmounts with its issue; focus left on <body> sits
      // outside the editor, where undo no longer reaches.
      containerRef.current?.focus({ preventScroll: true });
      if (fix.kind === 'markStepEnd') {
        void markStepAsTerminal(fix.stepId, 'NONE');
      } else {
        void markRulesAsTerminal(fix.stepId, fix.ruleIds, 'NONE');
      }
    },
    [containerRef, strandedRuleIds, markStepAsTerminal, markRulesAsTerminal]
  );

  // A focused toggle or row unmounts when an edit or an undo removes its last
  // issue; focus would then fall to <body>, where undo no longer reaches.
  const keepFocusInEditor = useCallback(
    (element: HTMLElement | null) => () => {
      if (element && element.ownerDocument.activeElement === element) {
        containerRef.current?.focus({ preventScroll: true });
      }
    },
    [containerRef]
  );

  const isShown = isOpen && isCanvasReady;
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const issuesToggleRef = useCallback(
    (toggle: HTMLButtonElement | null) => {
      toggleRef.current = toggle;
      const keepFocus = keepFocusInEditor(toggle);
      return () => {
        keepFocus();
        toggleRef.current = null;
      };
    },
    [keepFocusInEditor]
  );

  // Toggles what is shown: a click while the canvas loads keeps the sidebar open.
  const toggleIssues = useCallback(() => setIsOpen(!isShown), [isShown]);
  const showIssues = useCallback(() => setIsOpen(true), []);
  // `toggleRef` is null while the toggle is hidden at 0 issues.
  const closeIssues = useCallback(() => {
    setIsOpen(false);
    (toggleRef.current ?? containerRef.current)?.focus({ preventScroll: true });
  }, [containerRef]);

  const handleValidationRejected = useCallback(
    (error: PublishValidationError) => {
      setRejection(error);
      setIsOpen(true);
      toast.error(t('processDraft.publishErrors.title'));
    },
    [t]
  );

  const markers = useMemo<EditorMarkers>(() => {
    if (!isShown) return {};
    const nodeIdsWith = (flagged: (issues: readonly DraftIssue[]) => boolean) =>
      new Set(
        groups.flatMap(({ nodeId, issues }) => (nodeId !== null && flagged(issues) ? [nodeId] : []))
      );
    return {
      errorNodeIds: nonEmpty(nodeIdsWith(() => true)),
      // Gateway rule dots stay broader than the publish gate: they flag every
      // stranded rule, also on decisions that fail a step-level check first.
      incompleteRuleIdsByStep: nonEmpty(strandedRuleIds),
      disconnectedEventIds: nonEmpty(
        nodeIdsWith((issues) => issues.some((issue) => issue.kind === 'eventDisconnected'))
      )
    };
  }, [isShown, groups, strandedRuleIds]);

  const issuesByNodeId = useMemo(
    () =>
      new Map<string | null, readonly DraftIssue[]>(
        groups.map((group) => [group.nodeId, group.issues])
      ),
    [groups]
  );

  const contextValue = useMemo<DraftIssuesContextValue>(
    () => ({
      issuesByNodeId,
      focusRequest,
      goToIssue,
      runQuickFix,
      onFocusHandled,
      keepFocusInEditor
    }),
    [issuesByNodeId, focusRequest, goToIssue, runQuickFix, onFocusHandled, keepFocusInEditor]
  );

  return {
    groups,
    issueCount,
    isShown,
    issuesToggleRef,
    keepFocusInEditor,
    toggleIssues,
    showIssues,
    closeIssues,
    goToIssue,
    runQuickFix,
    handleValidationRejected,
    revealRequest,
    markers,
    contextValue
  };
}
